import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
  Bookmark,
  LayoutDashboard,
  Database,
  CalendarIcon,
  FileText,
  Search,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Bot,
  MessageCircle,
  LayoutTemplate,
  ExternalLink,
  Lightbulb,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { DATA_SYNCING_ON_HOME } from "@/lib/features";



/* ------------------------------------------------------------------ */
/*  Bookmarked Research Widget                                        */
/* ------------------------------------------------------------------ */
const BookmarkedResearchCard = () => {
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("conversations")
        .select("id, title, updated_at, profile_name")
        .eq("bookmarked", true)
        .is("deleted_at", null)
        .order("updated_at", { ascending: false })
        .limit(10);
      setConversations(data || []);
      setLoading(false);
    })();
  }, []);

  if (loading) return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Bookmark className="h-4 w-4 text-muted-foreground" />
          Research Chats
        </CardTitle>
      </CardHeader>
      <CardContent><WidgetLoader /></CardContent>
    </Card>
  );

  if (conversations.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between w-full">
          <CardTitle className="flex items-center gap-2 text-base">
            <Bookmark className="h-4 w-4 text-muted-foreground" />
            Research Chats
          </CardTitle>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate("/research")}>
            <MessageSquare className="h-3 w-3" />
            New Research
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/research?conv=${c.id}`)}
              className="flex items-center gap-2 w-full rounded-lg border border-border px-3 py-2 text-left transition-colors hover:bg-accent"
            >
              <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{c.title}</p>
                <p className="text-xs text-muted-foreground">{format(new Date(c.updated_at), "MMM d, yyyy")}</p>
              </div>
              <Bookmark className="h-3.5 w-3.5 shrink-0 fill-primary text-primary" />
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/*  Bookmarked Reports / Dashboards / Queries Widget                  */
/* ------------------------------------------------------------------ */
const BookmarkedItemsCard = () => {
  const [items, setItems] = useState<{ type: string; id: string; title: string; updated_at: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [dashRes, queryRes] = await Promise.all([
        supabase
          .from("dashboard_snapshots")
          .select("id, title, updated_at")
          .eq("bookmarked", true)
          .order("updated_at", { ascending: false })
          .limit(5),
        supabase
          .from("saved_queries" as any)
          .select("id, name, updated_at, is_report")
          .eq("bookmarked", true)
          .order("updated_at", { ascending: false })
          .limit(10),
      ]);

      const all = [
        ...(dashRes.data || []).map((d: any) => ({
          type: "Dashboard",
          id: d.id,
          title: d.title,
          updated_at: d.updated_at,
        })),
        ...((queryRes.data || []) as any[]).map((q: any) => ({
          type: q.is_report ? "Report" : "Query",
          id: q.id,
          title: q.name,
          updated_at: q.updated_at,
        })),
      ].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

      setItems(all);
      setLoading(false);
    })();
  }, []);

  const typeIcon = (type: string) => {
    if (type === "Dashboard") return <LayoutDashboard className="h-4 w-4 shrink-0 text-muted-foreground" />;
    if (type === "Report") return <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />;
    return <Search className="h-4 w-4 shrink-0 text-muted-foreground" />;
  };

  if (loading) return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
          Reports / Dashboards
        </CardTitle>
      </CardHeader>
      <CardContent><WidgetLoader /></CardContent>
    </Card>
  );

  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between w-full">
          <CardTitle className="flex items-center gap-2 text-base">
            <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
            Reports / Dashboards
          </CardTitle>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate("/dashboards")}>
            <LayoutDashboard className="h-3 w-3" />
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((item) => (
            <button
              key={`${item.type}-${item.id}`}
              onClick={() => navigate("/dashboards")}
              className="flex items-center gap-2 w-full rounded-lg border border-border px-3 py-2 text-left transition-colors hover:bg-accent"
            >
              {typeIcon(item.type)}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{item.title}</p>
                <p className="text-xs text-muted-foreground">{format(new Date(item.updated_at), "MMM d, yyyy")}</p>
              </div>
              <Badge variant="outline" className="text-[10px] shrink-0">
                {item.type}
              </Badge>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/*  Data Syncing Status Widget                                        */
/* ------------------------------------------------------------------ */
type TimeWindow = "5m" | "30m" | "1h" | "1d" | "7d" | "custom";

const timeOptions: { value: TimeWindow; label: string }[] = [
  { value: "5m", label: "Last 5 min" },
  { value: "30m", label: "Last 30 min" },
  { value: "1h", label: "Last 1 hour" },
  { value: "1d", label: "Last 1 day" },
  { value: "7d", label: "Last 7 days" },
  { value: "custom", label: "Custom" },
];

const getWindowDate = (window: TimeWindow): Date => {
  const now = new Date();
  switch (window) {
    case "5m":
      return new Date(now.getTime() - 5 * 60 * 1000);
    case "30m":
      return new Date(now.getTime() - 30 * 60 * 1000);
    case "1h":
      return new Date(now.getTime() - 60 * 60 * 1000);
    case "1d":
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case "7d":
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    default:
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
};

interface SyncSource {
  name: string;
  table: string;
  statusCol: string;
  dateCol: string;
  countCol?: string;
}

const syncSources: SyncSource[] = [
  { name: "Manual Uploads", table: "manual_upload", statusCol: "sync_status", dateCol: "updated_at" },
  { name: "Google Drive", table: "drive_files", statusCol: "sync_status", dateCol: "updated_at" },
  { name: "Gmail", table: "email_files", statusCol: "sync_status", dateCol: "updated_at" },
];

const DataSyncWidget = () => {
  const [window, setWindow] = useState<TimeWindow>("1d");
  const [customFrom, setCustomFrom] = useState<Date | undefined>();
  const [customTo, setCustomTo] = useState<Date | undefined>();
  const [stats, setStats] = useState<{ name: string; synced: number; pending: number; failed: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const sinceDate = useMemo(() => {
    if (window === "custom" && customFrom) return customFrom;
    return getWindowDate(window);
  }, [window, customFrom]);

  const untilDate = useMemo(() => {
    if (window === "custom" && customTo) return customTo;
    return new Date();
  }, [window, customTo]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const results = await Promise.all(
        syncSources.map(async (src) => {
          const { data } = await supabase
            .from(src.table as any)
            .select("sync_status, row_count")
            .gte(src.dateCol, sinceDate.toISOString())
            .lte(src.dateCol, untilDate.toISOString());

          const rows = (data as any[]) || [];
          return {
            name: src.name,
            synced: rows.filter((r) => r.sync_status === "synced").reduce((sum, r) => sum + (r.row_count || 0), 0),
            pending: rows.filter((r) => r.sync_status === "pending").length,
            failed: rows.filter((r) => r.sync_status === "error" || r.sync_status === "failed").length,
          };
        }),
      );
      setStats(results);
      setLoading(false);
    })();
  }, [sinceDate, untilDate]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={window} onValueChange={(v) => setWindow(v as TimeWindow)}>
          <SelectTrigger className="w-40 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timeOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {window === "custom" && (
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {customFrom ? format(customFrom, "MMM d") : "From"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customFrom}
                  onSelect={setCustomFrom}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <span className="text-xs text-muted-foreground">–</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {customTo ? format(customTo, "MMM d") : "To"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customTo}
                  onSelect={setCustomTo}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {loading ? (
        <WidgetLoader />
      ) : (
        <div className="space-y-2">
          {stats.map((s) => (
            <div key={s.name} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{s.name}</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-emerald-500">
                  <CheckCircle2 className="h-3 w-3" />
                  {s.synced.toLocaleString()} rows
                </span>
                {s.pending > 0 && (
                  <span className="flex items-center gap-1 text-amber-500">
                    <RefreshCw className="h-3 w-3" />
                    {s.pending} pending
                  </span>
                )}
                {s.failed > 0 && (
                  <span className="flex items-center gap-1 text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    {s.failed} failed
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                    */
/* ------------------------------------------------------------------ */
const WidgetLoader = () => (
  <div className="flex items-center justify-center py-8">
    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
  </div>
);

/* ------------------------------------------------------------------ */
/*  How It Works Section                                              */
/* ------------------------------------------------------------------ */
const howItWorksSteps = [
  {
    id: "connect",
    icon: Database,
    title: "Connect your data sources",
    description:
      "Connect databases, social channels, and enterprise software to your private DataBull cluster.",
    details: [
      "Supports PostgreSQL, MySQL, MongoDB, BigQuery, Snowflake, and 50+ more databases.",
      "Connect social channels like Facebook, Instagram, and Twitter for social analytics.",
      "Integrate enterprise software such as Salesforce, HubSpot, Shopify, and more.",
      "All data stays within your private cluster — secure and encrypted.",
    ],
    explorePath: "/data-connectors",
    exploreLabel: "Connect Data Sources",
  },
  {
    id: "research",
    icon: Search,
    title: "Research within your data to get business insights",
    description:
      "Ask natural language questions about your data and get instant, actionable insights.",
    details: [
      "Use plain English to query across all your connected data sources.",
      "Get visualizations, charts, and tables generated automatically.",
      "Deep-dive into trends, anomalies, and patterns without writing SQL.",
    ],
    examples: [
      "Why did my sales drop yesterday?",
      "Give me details about users who have provided 1-star reviews",
      "What are the top 10 products by revenue this month?",
    ],
    explorePath: "/research",
    exploreLabel: "Start Researching",
  },
  {
    id: "workflows",
    icon: Bot,
    title: "Build agentic workflows to automate day-to-day operations",
    description:
      "Create automated workflows that run on schedule or trigger based on events to keep you informed.",
    details: [
      "Schedule recurring reports and alerts to be delivered automatically.",
      "Set up event-driven triggers — get notified when metrics cross thresholds.",
      "Chain multiple actions together for complex automation pipelines.",
    ],
    examples: [
      "Send me an email about yesterday's sales data every day at 7 AM",
      "Alert me on Slack when daily revenue drops below $10,000",
      "Generate a weekly performance summary every Monday morning",
    ],
    explorePath: "/aiagents",
    exploreLabel: "Build Workflows",
  },
  {
    id: "dashboards",
    icon: LayoutDashboard,
    title: "Build standard dashboards & reports for your business",
    description:
      "Create beautiful, interactive dashboards and scheduled reports tailored to your business needs.",
    details: [
      "Drag-and-drop dashboard builder with charts, tables, KPIs, and more.",
      "Share dashboards with your team or embed them in your own applications.",
      "Schedule reports to be generated and sent automatically on a recurring basis.",
    ],
    explorePath: "/dashboards",
    exploreLabel: "View Dashboards",
  },
  {
    id: "messaging",
    icon: MessageCircle,
    title: "Connect WhatsApp and other messaging apps",
    description:
      "Do research directly within your favourite messaging channel — no need to open a browser.",
    details: [
      "Connect WhatsApp, Telegram, Slack, Google Chat, and Microsoft Teams.",
      "Ask questions about your data directly in the chat — get instant answers.",
      "Share insights with your team right where they already communicate.",
    ],
    explorePath: "/external-interface",
    exploreLabel: "Connect Messaging Apps",
  },
  {
    id: "templates",
    icon: LayoutTemplate,
    title: "Choose from thousands of predefined templates",
    description:
      "Get started instantly with ready-made templates based on your industry and use case.",
    details: [
      "Browse templates for e-commerce, SaaS, marketing, finance, and more.",
      "Each template comes with pre-built dashboards, queries, and workflows.",
      "Customize any template to fit your specific business requirements.",
    ],
    explorePath: "/advanced-features",
    exploreLabel: "Browse Templates",
  },
];

const HowItWorksSection = () => {
  const navigate = useNavigate();

  return (
    <div className="col-span-1 lg:col-span-2">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lightbulb className="h-4 w-4 text-muted-foreground" />
            How does it work?
          </CardTitle>
          <p className="text-xs text-muted-foreground">Get started with DataBull in a few simple steps</p>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {howItWorksSteps.map((step, idx) => (
              <AccordionItem key={step.id} value={step.id}>
                <AccordionTrigger className="gap-3 text-left hover:no-underline [&>svg]:shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <step.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Step {idx + 1}</span>
                      <p className="text-sm font-medium leading-snug">{step.title}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="ml-11 space-y-4">
                    <p className="text-muted-foreground">{step.description}</p>

                    <ul className="space-y-1.5">
                      {step.details.map((d, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          {d}
                        </li>
                      ))}
                    </ul>

                    {"examples" in step && step.examples && (
                      <div className="rounded-md border border-border bg-muted/50 p-3">
                        <p className="mb-2 text-xs font-medium text-muted-foreground">Examples</p>
                        <ul className="space-y-1">
                          {step.examples.map((ex: string, i: number) => (
                            <li key={i} className="text-sm italic text-foreground">
                              "{ex}"
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Button
                      size="sm"
                      onClick={() => navigate(step.explorePath)}
                      className="gap-1.5"
                    >
                      {step.exploreLabel}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Home Page                                                         */
/* ------------------------------------------------------------------ */
const Home = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isViewer = profile?.viewer && !profile?.admin && !profile?.super_admin;

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl font-semibold">Home</h1>
        <p className="text-sm text-muted-foreground">Overview of your workspace activity.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!isViewer && <BookmarkedResearchCard />}

        <BookmarkedItemsCard />

        {DATA_SYNCING_ON_HOME && !isViewer && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between w-full">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Database className="h-4 w-4 text-muted-foreground" />
                  Data Syncing Status
                </CardTitle>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate("/data-connectors")}>
                  <Database className="h-3 w-3" />
                  New Data Connector
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DataSyncWidget />
            </CardContent>
          </Card>
        )}

        <HowItWorksSection />
      </div>
    </div>
  );
};

export default Home;
